import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, CardMedia, Typography, TextField, Button, Chip, Box,
  CircularProgress, makeStyles, IconButton, Tooltip, Divider,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RoomIcon from '@material-ui/icons/Room';
import StarIcon from '@material-ui/icons/Star';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { liveTutorService } from '../services';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 4 },
  sub: { color: '#6b7280', fontSize: 13, marginBottom: 16 },
  searchRow: { display: 'flex', gap: 12, marginBottom: 20, maxWidth: 520 },
  card: { height: '100%', display: 'flex', flexDirection: 'column' },
  media: { height: 180, backgroundColor: '#eef1f5' },
  noPhoto: { height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eef1f5', color: '#9aa3af', fontSize: 40, fontWeight: 700 },
  name: { fontWeight: 700, fontSize: 15 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 2 },
  contact: { color: '#374151', fontSize: 13, marginTop: 4 },
  chips: { marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 },
  chip: { height: 22 },
  statusRow: { marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4 },
  rate: { marginTop: 8, fontWeight: 600, color: '#111827' },
  rating: { marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24 },
  actions: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginTop: 'auto' },
  spacer: { flex: 1 },
}));

type LiveTutor = {
  id: string;
  userId?: string | null;
  name: string;
  gender?: string | null;
  picture?: string | null;
  mobile?: string | null;
  email?: string | null;
  subjects: string[];
  classes?: string | null;
  area?: string | null;
  serviceAreas?: string[];
  pincode?: string | null;
  lat?: number | null;
  lng?: number | null;
  mode?: string | null;
  hourlyRate?: number | null;
  ratingAvg?: number | null;
  ratingCount?: number | null;
  years?: number | null;
  isActive?: boolean;
  acceptingBookings?: boolean;
  verifications?: { police?: boolean; degree?: boolean; id?: boolean };
  kycStatus?: 'none' | 'pending' | 'approved' | 'rejected';
};

// KYC status → chip color.
const kycChip = (status?: string): { label: string; color: string; bg: string } => {
  switch (status) {
    case 'approved': return { label: 'KYC verified', color: '#047857', bg: '#d1fae5' };
    case 'pending': return { label: 'KYC pending', color: '#b45309', bg: '#fef3c7' };
    case 'rejected': return { label: 'KYC rejected', color: '#b91c1c', bg: '#fee2e2' };
    default: return { label: 'No KYC', color: '#6b7280', bg: '#f3f4f6' };
  }
};

const LiveTeachersScreen: React.FC = () => {
  const classes = useStyles();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tutors, setTutors] = useState<LiveTutor[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await liveTutorService.list(search, page, 24);
    if (res.ok) {
      setTutors(res.data?.tutors || []);
      setTotal(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const applySearch = () => { setPage(1); setSearch(input.trim()); };

  const locationUrl = (t: LiveTutor) => {
    if (typeof t.lat === 'number' && typeof t.lng === 'number') {
      return `https://www.google.com/maps/search/?api=1&query=${t.lat},${t.lng}`;
    }
    const q = [t.area, t.pincode, 'Guwahati'].filter(Boolean).join(' ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };
  const hasLocation = (t: LiveTutor) =>
    (typeof t.lat === 'number' && typeof t.lng === 'number') || !!t.area || !!t.pincode;
  const hasPin = (t: LiveTutor) => typeof t.lat === 'number' && typeof t.lng === 'number';

  return (
    <div>
      <Typography variant="h5" className={classes.header}>Live Teachers</Typography>
      <Typography className={classes.sub}>
        Real onboarded teachers with live accounts. Preview only — {total} total.
      </Typography>

      <div className={classes.searchRow}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search name, mobile, email, subject, area, pincode…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
        />
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={applySearch}>Search</Button>
      </div>

      {loading ? (
        <Box p={6} display="flex" justifyContent="center"><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {tutors.map((t) => {
              const kyc = kycChip(t.kycStatus);
              const v = t.verifications || {};
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                  <Card className={classes.card} elevation={1}>
                    {t.picture
                      ? <CardMedia className={classes.media} image={t.picture} title={t.name} />
                      : <div className={classes.noPhoto}>{(t.name || '?').charAt(0).toUpperCase()}</div>}
                    <CardContent>
                      <Typography className={classes.name}>{t.name}</Typography>
                      <Typography className={classes.meta}>
                        {[t.area, t.classes ? `Class ${t.classes}` : null, t.years ? `${t.years} yrs` : null].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                      {t.mobile ? <Typography className={classes.contact}>📞 {t.mobile}</Typography> : null}
                      {t.email ? <Typography className={classes.meta}>{t.email}</Typography> : null}

                      <div className={classes.chips}>
                        {(t.subjects || []).slice(0, 4).map((s) => <Chip key={s} className={classes.chip} size="small" label={s} />)}
                      </div>

                      {t.ratingCount ? (
                        <span className={classes.rating}>
                          <StarIcon style={{ fontSize: 16, color: '#f59e0b' }} />
                          {(t.ratingAvg || 0).toFixed(1)} ({t.ratingCount})
                        </span>
                      ) : null}
                      {t.hourlyRate ? <Typography className={classes.rate}>₹{t.hourlyRate}/hr</Typography> : null}

                      <div className={classes.statusRow}>
                        <Chip
                          className={classes.chip}
                          size="small"
                          label={kyc.label}
                          style={{ color: kyc.color, backgroundColor: kyc.bg }}
                        />
                        {t.acceptingBookings
                          ? <Chip className={classes.chip} size="small" label="Open for bookings" style={{ color: '#047857', backgroundColor: '#d1fae5' }} />
                          : <Chip className={classes.chip} size="small" label="Not accepting" style={{ color: '#6b7280', backgroundColor: '#f3f4f6' }} />}
                        {t.isActive === false
                          ? <Chip className={classes.chip} size="small" label="Inactive" style={{ color: '#b91c1c', backgroundColor: '#fee2e2' }} />
                          : null}
                        {(v.police || v.degree || v.id) ? (
                          <Chip
                            className={classes.chip}
                            size="small"
                            icon={<VerifiedUserIcon style={{ fontSize: 14 }} />}
                            label={[v.police ? 'Police' : null, v.degree ? 'Degree' : null, v.id ? 'ID' : null].filter(Boolean).join(' · ')}
                            style={{ color: '#1d4ed8', backgroundColor: '#dbeafe' }}
                          />
                        ) : null}
                      </div>
                    </CardContent>

                    <Divider />
                    <div className={classes.actions}>
                      <Tooltip title={hasPin(t) ? 'Open exact location pin' : (hasLocation(t) ? 'Open area on map' : 'No location on file')}>
                        <span>
                          <IconButton
                            size="small"
                            color={hasPin(t) ? 'primary' : 'default'}
                            disabled={!hasLocation(t)}
                            onClick={() => window.open(locationUrl(t), '_blank', 'noopener')}
                          >
                            <RoomIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <span className={classes.spacer} />
                    </div>
                  </Card>
                </Grid>
              );
            })}
            {tutors.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">No live teachers match your search.</Typography>
              </Grid>
            )}
          </Grid>

          <div className={classes.pager}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Typography variant="body2">Page {page} / {totalPages}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveTeachersScreen;
